import React from "react";
import { PopUpConstants, shared } from "../../src/taskpane/config/constants";
import { IAuthTokenResultDto } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import authModule, { DialogMessageResponse, DialogMessageStatus, TokenResource } from "../../utilities/AuthModule";
import { logger } from "../../utilities/logger";

export function usePopUpAuth() {
    const [isSharePointAdminTokenAvailable, setIsSharePointAdminTokenAvailable] = React.useState<boolean>(authModule.getPersistedAuthState()?.sharePointAdminToken?.length > 0);
    const [isSharePointAdminTokenRequestInProgress, setIsSharePointAdminTokenRequestInProgress] = React.useState<boolean>(false);

    const authDialogRef = React.useRef<any>();

    const requestSharePointAdminToken = () => {
        let authState: IAuthTokenResultDto = authModule.getPersistedAuthState();
        let authUrl: URL = new URL(`${process.env.baseUri}/auth.html`);
        authUrl.searchParams.append(shared.params.app, "content_manager");
        authUrl.searchParams.append(shared.params.resource, TokenResource.SharePointAdmin.toString());
        authUrl.searchParams.append(shared.params.username, authState.account?.username);
        authUrl.searchParams.append("sharePointBaseUrl", authState.account?.sharePointBaseUrl);
        /**
         * adding winLeft and winTop to account for dual monitor
         * using screenLeft and screenTop for IE8 and earlier
         */
        const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
        const winTop = window.screenTop ? window.screenTop : window.screenY;
        /**
         * window.innerWidth displays browser window"s height and width excluding toolbars
         * using document.documentElement.clientWidth for IE8 and earlier
         */
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        const left = Math.max(0, ((width / 2) - (PopUpConstants.POPUP_WIDTH / 2)) + winLeft);
        const top = Math.max(0, ((height / 2) - (PopUpConstants.POPUP_HEIGHT / 2)) + winTop);

        authDialogRef.current = window.open(authUrl.href, `${PopUpConstants.POPUP_NAME_PREFIX}.${TokenResource.SharePointAdmin}`, `width=${PopUpConstants.POPUP_WIDTH}, height=${PopUpConstants.POPUP_HEIGHT}, top=${top}, left=${left}, scrollbars=yes`);
    }

    const processAuthDialogResponse = (event) => {
        if (event.origin != process.env.baseUri)
            return;

        try {
            if (event.data) {
                const messageFromDialog: DialogMessageResponse = event.data;
                if (messageFromDialog?.status === DialogMessageStatus.Success) {
                    if (messageFromDialog.result?.sharePointAdminToken?.length > 0) {
                        let authState: IAuthTokenResultDto = authModule.getPersistedAuthState();
                        if (authState) {
                            authState.sharePointAdminToken = messageFromDialog.result.sharePointAdminToken;
                            authState.sharePointAdminTokenExpiresOn = messageFromDialog.result.sharePointAdminTokenExpiresOn;
                            authModule.persistAuthState(authState);
                            setIsSharePointAdminTokenAvailable(true);
                        }
                    }
                }
            }
        } catch (error) {
            logger(error, "usePopUpAuth.tsx (processAuthDialogResponse)", event.data);
        }

        authDialogRef?.current?.close();
    }

    React.useEffect(() => {
        window.addEventListener("message", processAuthDialogResponse);
        if (!isSharePointAdminTokenAvailable) {
            (async () => {
                setIsSharePointAdminTokenRequestInProgress(true);
                const sharePointAdminToken: string = await authModule.getSharePointAdminAccessToken(true);
                if (sharePointAdminToken) {
                  setIsSharePointAdminTokenAvailable(true);
                }
                setIsSharePointAdminTokenRequestInProgress(false);
            })();
        }
        return () => window.removeEventListener("message", processAuthDialogResponse);
    }, [isSharePointAdminTokenAvailable]);

    return { isSharePointAdminTokenAvailable, isSharePointAdminTokenRequestInProgress, requestSharePointAdminToken }
}